/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type Filter, type FormGroup, type HttpResponse, type Rest, HttpService, I18nService } from '@dm/core';
import { FCPORT_RES_BATCH } from 'app/common/constants';
import { MO } from 'app/common/constants/mo';
import { ToolService } from 'app/common/services';
import { type LifPort } from 'app/common/types/mo';
import { FeatureConfig } from 'app/config/feature';
import { type Observable, forkJoin, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { type ETHPort, type Port } from '../../../../common/types/mo';
import { type DropdownItem } from '../lun-group/model';
import { PortGroupMemberTypeE } from './../port-group/port-group.const';


@Injectable({
    providedIn: 'root',
})
export class MappingService {
    constructor(
        private http: HttpService,
        private tool: ToolService,
    ) { }

    /**
     * 创建映射
     *
     * @param {*} param 参数
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns
     * @memberof MappingService
     */
    createMap(param, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('mapping.create');
        return this.http.post(rest, param, opt);
    }

    /**
     * 批量创建映射
     */
    createMapBatch(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('mapping.createBatch');
        return this.http.post(rest, params, options);
    }

    /**
     * 移除映射
     *
     * @param {*} param 参数
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns
     * @memberof MappingService
     */
    removeMap(param, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('mapping.remove');
        rest.setParams(param);
        return this.http.delete(rest, opt);
    }

    /**
     * 获取满足搜索条件的端口组
     *
     * @param {BaseRequestOptionsArgs} [options] 请求配置项
     * @param {*} [filter] 过滤条件
     * @returns
     * @memberof MappingService
     */
    getPortGroup(options?: BaseRequestOptionsArgs, filter?: Filter[], vstoreId?: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('portGroup.getBatch').setParams({ vstoreId });
        if (filter) {
            rest.setFilters(filter);
        }
        return this.http.get(rest, options);
    }

    /**
     * 搜索满足条件的主机
     *
     * @param {BaseRequestOptionsArgs} [options] 请求配置项
     * @param {*} [filter] 过滤条件
     * @returns
     * @memberof MappingService
     */
    searchAllHost(options?: BaseRequestOptionsArgs, filter?: Filter[]): Observable<HttpResponse> {
        const rest = this.http.makeUrl('host.getBatch');
        if (filter) {
            rest.setFilters(filter);
        }
        return this.http.get(rest, options);
    }

    /**
     * 查询可以映射给某个LUN的主机
     *
     * @param {*} id LUN ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @param {*} [filter] 过滤条件
     * @returns 可观察对象
     * @memberof MappingService
     */
    getHostsCanMapLun(id: string, opt?: BaseRequestOptionsArgs, filter?: Filter[]): Observable<any[]> {
        const rest = this.http.makeUrl('mapping.unassociateHost');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: id,
        });
        if (filter) {
            rest.setFilters(filter);
        }
        return this.tool.getAllData(rest, opt);
    }

    /**
     * 查询可以映射给某个LUN的主机
     *
     * @param {*} id LUN ID
     * @param {*} [filter] 过滤条件
     * @returns 可观察对象
     * @memberof MappingService
     */
    getHostsCanMapLunRest(id: string, filter?: Filter[], vstoreId?: string): Rest {
        const rest = this.http.makeUrl('mapping.unassociateHost').setParams({ vstoreId });
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: id,
        });
        if (filter) {
            rest.setFilters(filter);
        }
        return rest;
    }

    /**
     * 查询可以映射给某个LUN组的主机
     *
     * @param {*} id LUN组ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @param {*} [filter] 过滤条件
     * @returns 可观察对象
     * @memberof MappingService
     */
    getHostsCanMapLunGroup(id: string, opt?: BaseRequestOptionsArgs, filter?: Filter[]): Observable<any[]> {
        const rest = this.http.makeUrl('mapping.unassociateHost');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN_GROUP,
            ASSOCIATEOBJID: id,
        });
        if (filter) {
            rest.setFilters(filter);
        }
        return this.tool.getAllData(rest, opt);
    }

    /**
     * 查询可以映射给某个LUN组的主机
     *
     * @param {*} id LUN组ID
     * @param {*} [filter] 过滤条件
     * @returns 可观察对象
     * @memberof MappingService
     */
    getHostsCanMapLunGroupRest(id: string | number, filter?: Filter[], vstoreId?: string): Rest {
        const rest = this.http.makeUrl('mapping.unassociateHost');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN_GROUP,
            ASSOCIATEOBJID: id,
            vstoreId,
        });
        if (filter) {
            rest.setFilters(filter);
        }
        return rest;
    }

    /**
     * 查询可以映射给某个LUN的主机组
     *
     * @param {*} id LUN ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns
     * @memberof MappingService
     */
    getHostGroupsCanMapLun(id: string, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('mapping.unassociateHostGroup');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: id,
        });
        return this.http.get(rest, opt);
    }

    /**
     * 查询可以映射给某个LUN组的主机组
     *
     * @param {*} id LUN组ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @param {*} [filters] 过滤条件
     * @returns 可观察对象
     * @memberof MappingService
     */
    getHostGroupsCanMapLunGroup(id: string, opt?: BaseRequestOptionsArgs, filters?: Filter[]): Observable<any[]> {
        const rest = this.http.makeUrl('mapping.unassociateHostGroup');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN_GROUP,
            ASSOCIATEOBJID: id,
        });
        if (filters) {
            rest.setFilters(filters);
        }
        return this.tool.getAllData(rest, opt);
    }

    /**
     * 查询可以映射给某个LUN组的主机组
     *
     * @param {*} id LUN组ID
     * @param {*} [filters] 过滤条件
     * @returns 可观察对象
     * @memberof MappingService
     */
    getHostGroupsCanMapLunGroupRest(id: string | number, filters?: Filter[], vstoreId?: string): Rest {
        const rest = this.http.makeUrl('mapping.unassociateHostGroup');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN_GROUP,
            ASSOCIATEOBJID: id,
            vstoreId,
        });
        if (filters) {
            rest.setFilters(filters);
        }
        return rest;
    }

    /**
     * 查询映射给某个LUN的对象
     *
     * @param {*} id LUN ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns REST接口
     * @memberof MappingService
     */
    getLunMappedObjRest(id: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjects');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: id,
        });
        return rest;
    }

    /**
     * 查询映射给某个LUN的对象个数
     *
     * @param {*} id LUN ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns REST接口
     * @memberof MappingService
     */
    getLunMappedObjCountRest(id: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjectsCount');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: id,
        });
        return rest;
    }

    /**
     * 查询映射给某个LUN组的对象
     *
     * @param {*} id LUN组ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns REST接口
     * @memberof MappingService
     */
    getLunGroupMappedObjRest(id: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjects');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN_GROUP,
            ASSOCIATEOBJID: id,
        })
        .setGropFilters([
            [
                { field: 'hostGroupId', matchMode: '!!', value: '-1' },
            ],
            [
                { field: 'hostId', matchMode: '!!', value: '-1' },
            ],
        ]);
        return rest;
    }

    /**
     * 查询映射给某个LUN组的对象个数
     *
     * @param {*} id LUN组ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns REST接口
     * @memberof MappingService
     */
    getLunGroupMappedObjCountRest(id: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjectsCount');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.LUN_GROUP,
            ASSOCIATEOBJID: id,
        })
        .setGropFilters([
            [
                { field: 'hostGroupId', matchMode: '!!', value: '-1' },
            ],
            [
                { field: 'hostId', matchMode: '!!', value: '-1' },
            ],
        ]);
        return rest;
    }

    /**
     * 查询映射给某个主机的对象
     *
     * @param {*} id 主机ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns REST接口
     * @memberof MappingService
     */
    getHostMappedObjRest(id: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjects');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.HOST_TYPE,
            ASSOCIATEOBJID: id,
        });
        return rest;
    }

    /**
     * 查询映射给某个主机的对象个数
     *
     * @param {*} id 主机ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns REST接口
     * @memberof MappingService
     */
    getHostMappedObjCountRest(id: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjectsCount');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.HOST_TYPE,
            ASSOCIATEOBJID: id,
        });
        return rest;
    }

    /**
     * 查询映射给某个主机组的对象
     *
     * @param {*} id 主机组ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns REST接口
     * @memberof MappingService
     */
    getHostGroupMappedObjRest(id: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjects');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.HOST_GROUP_TYPE,
            ASSOCIATEOBJID: id,
        })
        .setFilters([
            { field: 'lunGroupId', matchMode: '!!', value: '-1' },
        ]);
        return rest;
    }

    /**
     * 查询映射给某个主机组的对象个数
     *
     * @param {*} id 主机组ID
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns REST接口
     * @memberof MappingService
     */
    getHostGroupMappedObjCountRest(id: string): Rest {
        const rest = this.http.makeUrl('mapping.mappedObjectsCount');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.HOST_GROUP_TYPE,
            ASSOCIATEOBJID: id,
        })
        .setFilters([
            { field: 'lunGroupId', matchMode: '!!', value: '-1' },
        ]);
        return rest;
    }

    /**
     * @param {*} param 参数对象，包含5种字段组合下发场景：
     *                   1：主机ID
     *                   2：主机组ID
     *                   3：主机ID和LUNID
     *                   4：主机ID和LUNGroupId
     *                   5：主机组ID和LUNGroupId
     * @param {{ [key: string]: any }} [filter] 过滤参数对象
     * @returns REST接口
     * @memberof MappingService
     */
    getHostLunRest(param: { [key: string]: any }, filter?: Filter[]): Rest {
        const rest = this.http.makeUrl('mapping.hostLunInfo');
        rest.setParams(param);
        if (filter) {
            rest.setFilters(filter);
        }
        return rest;
    }

    /**
     * @param {*} param 参数对象，包含5种字段组合下发场景：
     *                   1：主机ID
     *                   2：主机组ID
     *                   3：主机ID和LUNID
     *                   4：主机ID和LUNGroupId
     *                   5：主机组ID和LUNGroupId
     * @param {{ [key: string]: any }} [filter] 过滤参数对象
     * @returns REST接口
     * @memberof MappingService
     */
    getHostLunCountRest(param: { [key: string]: any }, filter?: Filter[]): Rest {
        const rest = this.http.makeUrl('mapping.hostLunInfoCount');
        rest.setParams(param);
        if (filter) {
            rest.setFilters(filter);
        }
        return rest;
    }

    /**
     * 修改HOST LUN 信息
     *
     * @param {{ [key: string]: any }} param 参数
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns 可观察对象
     * @memberof MappingService
     */
    modifyHostLunInfo(param: { [key: string]: any }, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('mapping.modifyHostLunInfo');
        return this.http.put(rest, param, opt);
    }

    /**
     * 获取支持的最大host lun ID 数
     *
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns 可观察对象
     * @memberof MappingService
     */
    getMaxNumOfHostLunIds(opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('mapping.maxNumOfHostLunIds');
        return this.http.get(rest, opt);
    }

    /**
     * 查询端口组中和主机没有连接的端口列表
     *
     * @param {BaseRequestOptionsArgs} [opt] 请求配置项
     * @returns 可观察对象
     * @memberof MappingService
     */
    getPorts(params, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('mapping.getPorts');
        if (params) {
            rest.setParams(params);
        }
        return this.http.get(rest, { ...opt, doException: false });
    }

    /**
     * 映射前查询主机与端口组之间的连接状况进行防呆
     */
    checkConfigToPortGroup(params, vstoreId: string, portType: PortGroupMemberTypeE):
            Observable<{ unlinkSourcePort: Port[]; isPortGroupEmpty: boolean; portList: any[]}> {
        const ETHPortRest = this.http.makeUrl('port.getETHPortBatch').setParams({ vstoreId })
            .setFilters([
                // 过滤容器端口
                { field: 'LOGICTYPE', value: '13_14', matchMode: '!!' },
            ]);
        const FCPortRest = this.http.makeUrl('port.getFCPortBatch').setParams({ vstoreId })
            .setFilters([
                // 过滤容器端口
                { field: 'LOGICTYPE', value: '13_14', matchMode: '!!' },
            ]);
        // 逻辑端口
        const LifPortRest = this.http.makeUrl('lif.getBatch').setParams({ vstoreId });

        const fcPort$ = !FeatureConfig.IsSupportFC ? of(FCPORT_RES_BATCH) :
            this.http.get(FCPortRest, { mask: true, doException: false });
        const ETHPort$ = this.http.get(ETHPortRest, { mask : true, doException: false });
        const LifPortRest$ = this.http.get(LifPortRest, { mask : true, doException: false });
        const unlinkSourcePort$ = this.getPorts(params, { mask : true, doException: false });

        // 查询端口组中没有和主机有连接的端口列表
        let isPortGroupEmpty = false;
        // 所有端口数据
        let allSourcePort: Port[] = [];
        // 端口组与主机未连接的端口
        let unlinkSourcePort: Port[] = [];
        let portList: string[] = [];

        if (portType === PortGroupMemberTypeE.Lif) {
            // 逻辑端口
            return forkJoin([LifPortRest$, unlinkSourcePort$])
            .pipe(
                tap((results) => {
                    const lifPorts: LifPort[] = results[0].data || [];
                    allSourcePort = lifPorts;

                    const ids = results[1]?.data[0]?.portList.replaceAll(/(\[|\])/g, '');
                    portList = ids ? ids.split(',') : [];
                    isPortGroupEmpty = results[1].data && results[1].data[0] && results[1].data[0].isPortGroupEmpty === '1';
                    if (portList.length) {
                        unlinkSourcePort = allSourcePort.filter((value) => portList.some((item) => item === value.ID));
                    }
                }),
                map(() => ({ unlinkSourcePort, isPortGroupEmpty, portList })),
            );
        } else {
            // 物理端口
            return forkJoin([fcPort$, ETHPort$, unlinkSourcePort$])
            .pipe(
                tap((results) => {
                    const fcPorts: FcPort[] = results[0].data || [];
                    const ethPorts: ETHPort[] = results[1].data || [];
                    const allPort: Port[] = [...fcPorts, ...ethPorts];
                    allPort.forEach((item) => this.setRoceType(item));
                    allSourcePort = allPort;

                    const ids = results[2]?.data[0]?.portList.replaceAll(/(\[|\])/g, '');
                    portList = ids ? ids.split(',') : [];
                    isPortGroupEmpty = results[2].data && results[2].data[0] && results[2].data[0].isPortGroupEmpty === '1';
                    if (portList.length) {
                        unlinkSourcePort = allSourcePort.filter((value) => portList.some((item) => item === value.ID));
                    }
                }),
                map(() => ({ unlinkSourcePort, isPortGroupEmpty, portList })),
            );
        }
    }

    /**
     * 处理端口组表单过滤所需数据
     * @param tableData 端口组表单数据数组
     */
    setRoceType(item: Port): string {
        return item.newType = item.TYPE === MO.ETH_PORT_TYPE ? item.TYPE + '_' + item.physicalType : String(item.TYPE);
    }

    /**
     * 给下拉菜单增加一个选项并选中
     *
     * @param {*} oldOptions 选项数组的引用
     * @param {*} newOption 新创建的一个选项
     * @param {*} formGroup formGroup对象
     * @param {*} formContolName formContol名
     * @memberof MappingService
     */
    addOptAndSelect(oldOptions: DropdownItem[], newOption: DropdownItem, formGroup: FormGroup, formContolName: string): void {
        const patch = {};
        patch[formContolName] = newOption.value;
        oldOptions.push(newOption);
        formGroup.patchValue(patch);
    }

    /**
     * 组装下拉选择框列表选项
     * 如果传入ID可检测下拉列表中是否存在该选项
     *
     * @param {*} oldOptions 选项数组的引用
     * @param {*} data 用于组装下拉选择列表的数据
     * @param {*} searchOptionId 查询ID，可检测下拉列表中是否存在该选项
     * @returns 下拉列表中是否存在传入ID对应的选项
     * @memberof MappingService
     */
    setupDropdownOptions(oldOptions: DropdownItem[], data, searchOptionId?: string | number): boolean {
        let findSearchedOption = false;
        const newOption = [];
        if (data.length > 0) {
            data.forEach(
                (item) => {
                    if (null != searchOptionId && searchOptionId === item.ID) {
                        findSearchedOption = true;
                    }
                    newOption.push(
                        {
                            label: item.NAME,
                            value: item.ID,
                        },
                    );
                },
            );
        }
        oldOptions.length = 1;
        oldOptions.splice(1, 0, ...newOption);
        return findSearchedOption;
    }

    /**
     * 组装下拉选择框选项数据
     *
     * @param {*} data 后台查回的原始数据
     * @returns 可赋值给组件的下拉选项数组
     * @memberof MappingService
     */
    getDropdownOptions(data): any[] {
        const option = [];
        if (data.length > 0) {
            data.forEach(
                (item) => {
                    option.push(
                        {
                            label: item.NAME,
                            value: item.ID,
                        },
                    );
                },
            );
        }
        return option;
    }

    /**
     * 传入ID检测下拉列表中是否存在该选项
     *
     * @param {*} options 下拉选项数组
     * @param {*} id 要检测的选项ID
     * @returns {boolean} 是否存在该选项
     * @memberof MappingService
     */
    searchOptById(options: DropdownItem[], id: string | number): boolean {
        let findSearchedOption = false;
        if (options.length > 0) {
            for (let i = 0, len = options.length; i < len; i++) {
                if (id === options[i].value) {
                    findSearchedOption = true;
                    break;
                }
            }
        }
        return findSearchedOption;
    }
}

@Injectable({
    providedIn: 'root',
})
export class MappingStrService {
    constructor(
        private i18nSvc: I18nService,
    ) { }


    i18n = {
        vstoreLabel: this.get('common.owningVstore_lab'),
        vstoreName: this.get('vstore_name_lab'),
        vstoreId: this.get('vstore_id_lab'),
        notEnoughErr: this.get('settings.userAndRole_roleManagement_task_add_noSelectObject_tip'),
        map: this.get('common.map_button'),
        mapLungroup: this.get('storage.lunGroup_task_map_title'),
        mapLun: this.get('storage.lun_task_mapLUN_title'),
        mapLunNotConnectHost: (portGroupName: string): string =>
            this.get('storage.lun_task_map_notconnectHost_confirm_msg', [portGroupName]),
        mapLunNotConnectHostGroup: (portGroupName: string): string =>
            this.get('storage.lun_task_map_notconnectHostofGroup_confirm_msg', [portGroupName]),
        portGroupEmptyTip: (portGroupName: string): string => this.get('storage.lun_task_map_portgroupEmpty_confirm_msg', [portGroupName]),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        createBtn: this.get('common.create_button'),
        selectLunIntro: this.get('host.host_task_mappingLun_selectLUN_lab'),
        selectLunGroupIntro: this.get('host.host_task_mappingLunGroup_selectLunGroup_lab'),
        lunGroupMustSelect: this.get('host.mapping_create_selectLUNGroup_msg'),
        new: this.get('common.newCreate_checkbox'),
        existing: this.get('common.Existed_checkbox'),
        name: this.get('common.name_column'),
        pleaseSelect: this.get('common.select_lab'),
        hostLunId: this.get('host.host_task_mappingLun_hostLunID_lab'),
        startHostLunId: this.get('host.host_task_mappingLun_startID_lab'),
        automatic: this.get('common.EnumDefine_STARTUP_MODE_E_AUTO'),
        manual: this.get('common.EnumDefine_STARTUP_MODE_E_MANUAL'),
        showMappedLuns: this.get('host.host_task_mappingLun_existed_mappedLun_lab'),
        showMappedLunGroups: this.get('host.host_task_mappingLunGroup_existed_mappedLunGroup_lab'),
        createMap: this.get('rest.createMaping_msg'),
        rightTabelMustSelect: this.get('host.host_task_mappingLun_selectOne_lab'),
        createMappingSuccess: this.get('rest.createMapping_success_msg'),
        unmap: this.get('host.hostGroup_task_cancleMap_lab'),
        unmapObject: this.get('host.hostGroup_task_UnmapObject_lab'),
        unmapLun: this.get('storage.lun_task_unmapLUN_title'),
        unmapLunGroup: this.get('storage.lunGroup_task_unmap_title'),
        unmapIntro: this.get('host.hostGroup_task_cancleMap_selectObject_lab'),
        selectedLuns: this.get('common.selectedLun_column', true),
        host: this.get('common.host_title'),
        hosts: this.get('common.hosts_title'),
        HostsorHostGroup: this.get('common.HostsorHostGroup'),
        availableObjects: this.get('common.available', [this.get('common.objects_label')]),
        selectedObjects: this.get('common.selected', [this.get('common.objects_label')]),
        hostGroup: this.get('common.hostGroup_title'),
        hostGroupTitle: this.get('common.hostGroup_list_title'),
        object: this.get('common.object_label'),
        unmapSuccess: this.get('rest.removeMapping_success_msg'),
        hostOrGroup: this.get('common.hostOrGroup_lab'),
        lunOrGroup: this.get('common.lunOrGroup_lab'),
        lunGroup: this.get('storage.LunGroup_term_LUNGroup_column'),
        mappingType: this.get('host.host_view_toplogy_mappingType_lab'),
        mappingCreateTitle: this.get('storage.lun_task_map_msg_title'),
        unMappingTitle: this.get('storage.lun_task_cancleMap_msg_title'),
        available: this.get('common.available'),
        selected: this.get('common.selected'),
        lunToHostTip: (hostName: string): string => this.get('storage.lun_task_map_toHost_list_confirm_msg', [hostName]),
        lunGrpToHostGrpTip: (lunGroupName: string, hostGroupName: string): string =>
            this.get('storage.lunGroup_task_map_toHostGroup_msg', [lunGroupName, hostGroupName]),
        lunGrpToHostTip: (lunGroupName: string, hostName: string): string =>
            this.get('storage.lunGroup_task_map_toHost_msg', [lunGroupName, hostName]),
        lunUnmapToHostTip: this.get('storage.lun_task_cancleMap_toHost_msg'),
        lunGrpUnmapToHostGrpTip: (lunGroup: string, hostGroup: string): string =>
            this.get('storage.lunGroup_task_cancleMap_toHostGroup_msg', [lunGroup, hostGroup]),
        lunGrpUnmapToHostTip: (lunGroup: string, host: string): string =>
            this.get('storage.lunGroup_task_cancleMap_toHost_msg', [lunGroup, host]),
        modifyMapBetweenLunHostPortG: this.get('storage.lun_task_modifyMap_toHost_HostConGuide_msg'),
        advancedLab: this.get('advance_label'),

        // 删除LUN，主机
        unmapLun2HostTips: this.get('storage.lun_task_cancleMap_toHost_HostConGuide_msg'),
        unmapLun2HostTipsFor2910: this.get('compute.lun_task_cancleMap_toHost_HostConGuide_msg'),
        // 删除LUN组，主机
        unmapLungroup2HostTips: this.get('storage.lunGroup_task_cancleMap_toHost_HostConGuide_msg'),
        // 删除LUN组，主机组
        unmapLungroup2HostGroupTips: this.get('storage.lunGroup_task_cancleMap_toHostGroup_HostConGuide_msg'),
        // 删除LUN或者LUN组、主机
        unmapLunAndLG2HostTips: this.get('storage.lungroup_task_cancleMap_confirm_msg'),
        unmapLunAndLG2HostTipsFor2910: this.get('compute.lungroup_task_cancleMap_confirm_msg'),
        // 删除LUN组、主机或者主机组
        unmapLG2HostOrHGTips: this.get('storage.lun_task_cancleMap_confirm_msg'),
        unmapPeLunTitle: this.get('storage.vvol_task_unmappingPELUN_tittle'),
        enableLab: this.get('common.enable_label'),
        fourceHostLunIDSameLab: this.get('host.host_task_mappingLun_fourceHostLunIDSame_lab'),
        fourceHostLunIDSametTip: this.get('host.host_task_mappingLun_fourceHostLunIDSame_tip'),
        selectedLab: this.get('protection.snapshot_task_create_selectedLUN_lab'),
        unmapChannelTitle: this.get('storage.channelLUN_task_unmap_title'),
        unmapChannelLunContent: this.get('storage.channelLUN_task_unmap_confirm_msg'),
    };

    // 映射类型数字转文本用
    mappingTypeStr = {
        0: this.get('common.mapping_unMapped_value'),
        1: this.get('common.host_title'),
        2: this.get('common.hostGroup_list_title'),
        3: this.get('common.objectType_LUN'),
        4: this.get('common.lungroup_checkbox'),
    };

    get(key: string, params?: boolean | any[], colon?: boolean): string {
        return this.i18nSvc.get(key, params, colon);
    }
}
